import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import StageTimer from "../game/StageTimer";
import { isHallStageScorePresent } from "../hall_1/medalDisplayData";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
} from "../hall_1/hallTimer";
import {
  HALL2_HOTSPOT_LABELS,
  HALL2_HOTSPOT_ORDER,
  HALL2_HOTSPOT_ROUTES,
  HALL2_STAGE_START,
  resolveActiveHall2Hotspot,
  hydrateHall2ProgressFromStage,
  getHall2HubRedirect,
  readHall2Completed,
} from "./heritageHallData";
import { ensureHallHotspotStyles, styleHallHotspot } from "../hall_1/hallHotspotFx";
import { setupHallPageBodyBackground, isMobViewport } from "../game/gameStageBackground";
import blurDesk from "../img/blur_Desk.jpg";
import blurMob from "../img/blur_mob.jpg";
import "../fonts/breuer-headline.css";
import "./hall_2.css";

/** Large Illustrator SVG — served from /public (SVGR cannot parse embedded binary). */
const HALL2_DESK_SVG = `${process.env.PUBLIC_URL || ""}/hall_2/hall2.svg`;
const HALL2_MOB_SVG = `${process.env.PUBLIC_URL || ""}/hall_2/hall2_mob.svg`;

function useHallSvgUrl(deskUrl, mobUrl) {
  const [svgUrl, setSvgUrl] = useState(() =>
    typeof window !== "undefined" && isMobViewport() ? mobUrl : deskUrl
  );

  useEffect(() => {
    const sync = () => {
      const next = isMobViewport() ? mobUrl : deskUrl;
      setSvgUrl((prev) => (prev === next ? prev : next));
    };
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
    };
  }, [deskUrl, mobUrl]);

  return svgUrl;
}

const ALL_HOTSPOT_IDS = HALL2_HOTSPOT_ORDER;

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

/** Hotspots whose puzzle stages are already scored in DB / session. */
function resolveCompletedHall2Hotspots(stage) {
  const completed = new Set(readHall2Completed());
  HALL2_HOTSPOT_ORDER.forEach((id, index) => {
    const stageNumber = HALL2_STAGE_START + index;
    if (isHallStageScorePresent(stage, stageNumber)) completed.add(id);
  });
  return [...completed];
}

/** Hall of Heritage — interactive SVG hub (stages 7+). */
export default function Hall2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = storedUser?.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const objectRef = useRef(null);
  const elapsedRef = useRef(0);
  const stageRowIdRef = useRef("");
  const hallSvgUrl = useHallSvgUrl(HALL2_DESK_SVG, HALL2_MOB_SVG);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [progressReady, setProgressReady] = useState(false);
  const [svgReady, setSvgReady] = useState(false);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [completedHotspots, setCompletedHotspots] = useState([]);
  const [hall1Complete, setHall1Complete] = useState(false);
  const [stageRecord, setStageRecord] = useState(null);

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-1"));
  }, [dispatch]);

  useEffect(() => {
    return setupHallPageBodyBackground(document.body, {
      className: "hall2-page",
      deskForeground: blurDesk,
      mobForeground: blurMob,
    });
  }, []);

  useEffect(() => {
    setSvgReady(false);
  }, [hallSvgUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const champsDone = isHallStageScorePresent(stage, 6);
      setHall1Complete(champsDone);

      if (stage) {
        hydrateHall2ProgressFromStage(stage);
        const hubRedirect = getHall2HubRedirect(stage);
        if (hubRedirect) {
          navigate(hubRedirect, {
            replace: true,
            state: {
              time: formatSecondsToClock(sec),
              elapsedSec: sec,
              points: Number(stage?.stage10) || 0,
            },
          });
          return;
        }
        setActiveHotspot(resolveActiveHall2Hotspot(stage));
        setCompletedHotspots(resolveCompletedHall2Hotspots(stage));
        setStageRecord(stage);
      } else {
        setActiveHotspot(null);
        setCompletedHotspots([]);
        setStageRecord(null);
      }

      stageRowIdRef.current = stageId || "";
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTimerReady(true);
      setProgressReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowIdRef.current,
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      save();
    };
  }, [timerReady, backendBase, adminToken, storedUser, isDemoBypass]);

  const handleHotspotClick = useCallback(
    (id) => {
      const route = HALL2_HOTSPOT_ROUTES[id];
      if (route) navigate(route);
    },
    [navigate]
  );

  const bindHotspots = useCallback(() => {
    const obj = objectRef.current;
    if (!obj || !progressReady) return;
    let doc = null;
    try {
      doc = obj.contentDocument;
    } catch {
      doc = null;
    }
    if (!doc) return;

    const svg = doc.querySelector("svg");
    if (svg) {
      svg.style.width = "100%";
      svg.style.height = "100%";
      svg.setAttribute("preserveAspectRatio", "none");
    }

    ensureHallHotspotStyles(doc);
    const completedSet = new Set(completedHotspots);

    ALL_HOTSPOT_IDS.forEach((id) => {
      let el = doc.getElementById(id);
      if (!el) {
        console.warn(`[Hall2] missing hotspot id: ${id}`);
        return;
      }

      const fresh = el.cloneNode(true);
      el.parentNode?.replaceChild(fresh, el);
      el = fresh;

      const enabled = id === activeHotspot && Boolean(HALL2_HOTSPOT_ROUTES[id]);
      const active = enabled;
      const completed = !active && completedSet.has(id);
      styleHallHotspot(el, { enabled, active, completed });
      el.setAttribute("aria-label", HALL2_HOTSPOT_LABELS[id] || id);
      el.setAttribute("aria-disabled", enabled ? "false" : "true");

      if (!enabled) return;

      const onActivate = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleHotspotClick(id);
      };

      el.addEventListener("click", onActivate);
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") onActivate(e);
      });
    });

    setSvgReady(true);
  }, [handleHotspotClick, activeHotspot, completedHotspots, progressReady]);

  useEffect(() => {
    const obj = objectRef.current;
    if (!obj) return undefined;

    if (obj.contentDocument?.querySelector("svg")) {
      bindHotspots();
    }

    obj.addEventListener("load", bindHotspots);
    return () => {
      obj.removeEventListener("load", bindHotspots);
    };
  }, [bindHotspots, hallSvgUrl]);

  return (
    <div className="hall2-escape">
      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={readTotalScoreFromStage(stageRecord)}
        />
      </div>

      <object
        ref={objectRef}
        className="hall2-svg"
        data={hallSvgUrl}
        type="image/svg+xml"
        aria-label="Hall of Heritage"
      >
        <p className="hall2-svg-fallback">Unable to load hall scene.</p>
      </object>

      {!svgReady ? (
        <p className="hall2-loading" aria-live="polite">
          Loading hall…
        </p>
      ) : null}

      {progressReady && !hall1Complete ? (
        <p className="hall2-gate" role="status">
          Complete the Hall of Champions to unlock this gallery.
        </p>
      ) : null}

      {progressReady && hall1Complete && activeHotspot && HALL2_HOTSPOT_LABELS[activeHotspot] ? (
        <p className="hall2-hotspot-toast" role="status">
          Click: {HALL2_HOTSPOT_LABELS[activeHotspot]}
        </p>
      ) : null}

      {progressReady && hall1Complete && !activeHotspot ? (
        <p className="hall2-gate hall2-gate--done" role="status">
          Hall of Heritage restored.
        </p>
      ) : null}
    </div>
  );
}
